import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { Button } from 'antd';
import Search from 'antd/es/transfer/search';
import { toast } from 'react-toastify';
import TableSection from '../tables/TableSection';
import DeleteModal from '../modals/DeleteModal';
import Spinner from '../elements/Spinner/Spinner';
import protocolSubcategoryApi from '../../api/protocol-subcategory-api';
import styles from '../../styles/users.module.scss';

const badgeStyle = {
  display: 'inline-block',
  padding: '2px 10px',
  borderRadius: '12px',
  fontSize: '12px',
  fontWeight: 500,
};

// One tab of the protocols page per protocol category (Bolesti, Životni
// stil, ...): list, search, toggle and delete of that category's items.
export default function SubcategoryTab({ category, categoryLabel }) {
  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [toggleLoadingId, setToggleLoadingId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    const { data, success, message } = await protocolSubcategoryApi.getAll(page, 10, {
      category,
      searchTerm: debouncedSearch,
    });
    if (success) {
      setItems(data.items);
      setTotalPages(data.totalPages);
    } else {
      toast.error(message || 'Greška pri učitavanju stavki');
    }
    setLoading(false);
  }, [category, page, debouncedSearch]);

  useEffect(() => {
    load();
  }, [load]);

  const handleToggleActive = async (item) => {
    setToggleLoadingId(item._id);
    const { success, message } = await protocolSubcategoryApi.update(item._id, {
      isActive: !item.isActive,
    });
    if (success) {
      toast.success(item.isActive ? 'Stavka deaktivirana' : 'Stavka aktivirana');
      load();
    } else {
      toast.error(message || 'Greška pri promeni statusa');
    }
    setToggleLoadingId(null);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    const { success, message } = await protocolSubcategoryApi.remove(deleteTarget._id);
    if (success) {
      toast.success('Stavka obrisana');
      setDeleteTarget(null);
      load();
    } else {
      toast.error(message || 'Greška pri brisanju');
    }
    setDeleting(false);
  };

  const columns = [
    { key: 'name', label: 'Naziv', render: (_, row) => row.name?.sr || '-' },
    {
      key: 'isActive',
      label: 'Status',
      render: (val) => (
        <span
          style={{
            ...badgeStyle,
            backgroundColor: val ? '#e6f4ea' : '#fdecea',
            color: val ? '#1e7e34' : '#c0392b',
          }}
        >
          {val ? 'Aktivna' : 'Neaktivna'}
        </span>
      ),
    },
  ];

  const actions = [
    {
      key: 'edit',
      label: 'Izmeni',
      icon: <img src="/assets/icons/edit-icon.svg" alt="Izmeni" />,
      onClick: (item) => navigate(`/protokoli/podkategorije/izmeni/${item._id}`),
    },
    {
      key: 'toggle',
      label: (item) => (item.isActive ? 'Deaktiviraj' : 'Aktiviraj'),
      icon: <img src="/assets/icons/verification.svg" alt="Toggle" />,
      onClick: handleToggleActive,
      disabled: (item) => toggleLoadingId === item._id,
    },
    {
      key: 'delete',
      label: 'Obriši',
      icon: <img src="/assets/icons/delete.svg" alt="Obriši" />,
      danger: true,
      className: 'delete',
      onClick: (item) => setDeleteTarget(item),
    },
  ];

  return (
    <div className={styles.wrapper}>
      <div className={styles.filters}>
        <div className={styles.filters__left}>
          <Search
            placeholder={`Pretraži — ${categoryLabel}`}
            allowClear
            enterButton
            className="search-input"
            onChange={(e) => setSearch(e.target.value)}
            width="auto"
          />
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <Button
            className="btn-primary"
            type="primary"
            onClick={() => navigate(`/protokoli/podkategorije/dodaj?category=${category}`)}
          >
            Dodaj stavku
          </Button>
        </div>
      </div>
      {loading ? (
        <Spinner size="lg" />
      ) : (
        <TableSection
          columns={columns}
          actions={actions}
          data={items}
          emptyState={{
            icon: '/assets/icons/no-users.svg',
            title: 'Nema stavki',
            description: `Kreirajte prvu stavku — ${categoryLabel}`,
          }}
          showPagination={items.length > 0}
          currentPage={page}
          totalPages={totalPages}
          onPageChange={setPage}
          onSearch={() => {}}
        />
      )}

      <DeleteModal
        visible={!!deleteTarget}
        onDelete={handleDelete}
        onCancel={() => setDeleteTarget(null)}
        loading={deleting}
        title="Brisanje stavke"
        message={`Da li ste sigurni da želite da obrišete "${deleteTarget?.name?.sr}"? Biće uklonjena i sa svih protokola.`}
        buttonText="Obriši stavku"
        cancelText="Odustani"
      />
    </div>
  );
}

SubcategoryTab.propTypes = {
  category: PropTypes.string.isRequired,
  categoryLabel: PropTypes.string.isRequired,
};
